import React, { useState } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import './Dashboard.css';
import { useAuth } from '../../context/AuthContext';

function StatCard({ title, value, change, up, icon }) {
  return (
    <div className="stat-card">
      <div className="stat-icon">{icon}</div>
      <div className="stat-info">
        <div className="stat-title">{title}</div>
        <div className="stat-value">{value}</div>
        <div className={`stat-change ${up ? 'up' : 'down'}`}>{up ? '▲' : '▼'} {change}</div>
      </div>
    </div>
  );
}

function MiniBar({ data }) {
  if (!data || data.length === 0) return <div style={{ padding: 16, color: 'var(--text-muted)' }}>No sales data yet</div>;
  const max = Math.max(...data.map(d => (Number(d.diesel) || 0) + (Number(d.petrol) || 0) + (Number(d.cng) || 0)), 1);
  return (
    <div className="mini-chart">
      {data.map((d, i) => (
        <div key={i} className="bar-group">
          <div className="bars">
            <div className="bar bar-diesel" style={{ height: `${((Number(d.diesel) || 0) / max) * 80}px` }} />
            <div className="bar bar-petrol" style={{ height: `${((Number(d.petrol) || 0) / max) * 80}px` }} />
            <div className="bar bar-cng" style={{ height: `${((Number(d.cng) || 0) / max) * 80}px` }} />
          </div>
          <div className="bar-label">{d.sale_date ? d.sale_date.slice(5) : d.day}</div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { canAccess } = useAuth();
  const [date] = useState(today());
  const { data, loading, error } = useApi(() => api.dashboard.get(date), [date], null);
  const tanks = data?.tanks || [];
  const trend = data?.weekly_trend || [];
  const recent = data?.recent || [];
  if (!canAccess('dashboard')) {
    return (
      <div className="dashboard-page">
        <ErrBanner msg="Access Denied: You do not have permission to access dashboard." />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h2 className="page-title">Dashboard</h2>
        <span className="date-badge">📅 {fmtDate(date)} | Shift 1</span>
      </div>
      {loading && <Spinner />}
      {error && <ErrBanner msg={error} />}
      {data && <>
        <div className="stats-grid">
          <StatCard title="Today's Sales" value={`₹${fmtNum(data.today_sales || 0)}`} change="Today" up={true} icon="💰" />
          <StatCard title="Active Pumps" value={data.active_pumps ?? '—'} change="Online" up={true} icon="⛽" />
          <StatCard title="Credit Pending" value={`₹${fmtNum(data.credit_pending || 0)}`} change="Outstanding" up={false} icon="💳" />
          <StatCard title="Fuel in Stock" value={`${fmtNum(tanks.reduce((s, t) => s + Number(t.current_stock || 0), 0))} L`} change="All Tanks" up={true} icon="🛢️" />
        </div>
        <div className="dashboard-row">
          <div className="section-card chart-card">
            <div className="section-header">Weekly Sales Trend (₹)</div>
            <div style={{ padding: 16 }}>
              <MiniBar data={trend} />
              <div className="chart-legend">
                <span className="legend-item"><span className="dot dot-diesel" />Diesel</span>
                <span className="legend-item"><span className="dot dot-petrol" />Petrol</span>
                <span className="legend-item"><span className="dot dot-cng" />CNG</span>
              </div>
            </div>
          </div>
          <div className="section-card stock-card">
            <div className="section-header">Tank Stock Levels</div>
            <div className="stock-list">
              {tanks.map(t => {
                const pct = Math.round((Number(t.current_stock) / Number(t.capacity)) * 100) || 0;
                const color = t.product_code === 'MS' ? '#e53e3e' : t.product_code === 'HSD' ? '#1a6fad' : '#2d9e5f';
                return (
                  <div key={t.id} className="stock-item">
                    <div className="stock-name">{t.tank_no} ({t.product_code})</div>
                    <div className="stock-bar-wrap"><div className="stock-bar" style={{ width: `${pct}%`, background: color }} /></div>
                    <div className="stock-nums"><span>{fmtNum(t.current_stock)} L</span><span className="stock-pct">{pct}%</span></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="section-card">
          <div className="section-header">Recent Transactions</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>Ref</th>
                <th style={{ textAlign: 'left' }}>Date</th>
                <th style={{ textAlign: 'left' }}>Type</th>
                <th>Amount (₹)</th>
                <th style={{ textAlign: 'left' }}>Status</th>
              </tr></thead>
              <tbody>
                {recent.map((t, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace', color: 'var(--primary)' }}>{t.ref || t.id}</td>
                    <td>{fmtDate(t.txn_date)}</td><td>{t.type}</td>
                    <td className="right-align">₹{fmtNum(t.amount || 0)}</td>
                    <td><span className={`badge ${t.status === 'Paid' ? 'badge-success' : t.status === 'Pending' ? 'badge-warning' : t.status === 'Received' ? 'badge-info' : 'badge-primary'}`}>{t.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>}
    </div>
  );
}
